import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { useSession } from '@/shared/hooks/useSession'
import { InstallationCreatePayload, InstallationWithResources } from '../api/InstallationModel'
import { OperatorOrganization } from '@/features/auth/api/AuthModel'
import { NON_EU_COUNTRIES } from '@/common'

export const INSTALLATIONS_QUERY_KEY = 'installations'

export const areMandatoryInfoToCreateInstallationCompleted = (
  organization: OperatorOrganization,
  country: string,
  doesOrganizationRequiresLocalAddress: boolean
) => {
  const nonEuCountry = NON_EU_COUNTRIES.find(nonEuCountry => nonEuCountry.value === country)
  const isPostCodeRequired = nonEuCountry?.isPostCodeRequired ?? true

  if (doesOrganizationRequiresLocalAddress) {
    return Boolean(
      (isPostCodeRequired ? organization.localPostCode : true) &&
      (isPostCodeRequired ? organization.postCode : true) &&
      organization.localCity &&
      organization.city &&
      organization.contactName &&
      organization.contactPhoneNumber &&
      organization.contactEmail
    )
  }

  return Boolean(
    (isPostCodeRequired ? organization.postCode : true) &&
    organization.city &&
    organization.contactName &&
    organization.contactPhoneNumber &&
    organization.contactEmail
  )
}

export const useInstallations = () => {
  const queryClient = useQueryClient()
  const {
    session: {
      organization: { id: organizationId }
    }
  } = useSession()

  return useQuery<InstallationWithResources[]>({
    queryKey: [INSTALLATIONS_QUERY_KEY],
    queryFn: async () => {
      const res = await apiClient.get<{
        installations: InstallationWithResources[]
      }>(`/v1/organizations/${organizationId}/installations`)

      for (const installation of res.data.installations) {
        queryClient.setQueryData<InstallationWithResources>(
          [INSTALLATIONS_QUERY_KEY, installation.id],
          installation
        )
      }

      return res.data.installations
    },
    staleTime: Infinity
  })
}

export const useInstallation = (organizationId: string, installationId: string) => {
  return useQuery({
    queryKey: [INSTALLATIONS_QUERY_KEY, installationId],
    queryFn: async () => {
      const res = await apiClient.get<InstallationWithResources>(
        `/v1/organizations/${organizationId}/installations/${installationId}`
      )
      return res.data
    },
    enabled: !!organizationId && !!installationId
  })
}

export const useCreateInstallation = () => {
  const queryClient = useQueryClient()
  const {
    session: {
      organization: { id: organizationId }
    }
  } = useSession()

  return useMutation({
    mutationFn: async (payload: InstallationCreatePayload) => {
      const res = await apiClient.post<InstallationWithResources>(
        `/v1/organizations/${organizationId}/installations`,
        payload
      )

      await queryClient.fetchQuery({
        queryKey: [INSTALLATIONS_QUERY_KEY]
      })

      return res.data
    }
  })
}

export const useUpdateInstallation = (installationId: string) => {
  const queryClient = useQueryClient()
  const {
    session: {
      organization: { id: organizationId }
    }
  } = useSession()

  return useMutation({
    mutationFn: async (payload: InstallationCreatePayload) => {
      const res = await apiClient.put<InstallationWithResources>(
        `/v1/organizations/${organizationId}/installations/${installationId}`,
        payload
      )

      await queryClient.fetchQuery({
        queryKey: [INSTALLATIONS_QUERY_KEY]
      })

      return res.data
    }
  })
}
